/**
 * ApsParamConformanceTable.jsx — the per-APS-field Checks detail view.
 *
 * Renders buildApsParamConformanceRows()' output
 * (frontend/src/utils/buildApsParamConformance.js) for APS mobile-SDK DISPLAY
 * requests: what each body field should be (spec layer, always on; BU layer,
 * only when an App Pack matched — docs/aps-param-conformance-spec.md §3.1),
 * what this session actually sent, and how many ELIGIBLE requests FAIL it.
 *
 * Sibling of ParamConformanceTable.jsx with ONE column added: Platform.
 * `pj.api`, `pj.att` and `skadn` are iOS-only, so a platform-blind table
 * renders them as failures on every Android session — the exact bug §1.7 found
 * in our own knowledge pack.
 *
 * Three things this table must never do (spec §2/§3.1, skill traps):
 *  - Render an eligible-0 row as a pass. "Not evaluated" is a different claim.
 *  - Render an off-platform field as a failure OR a pass. It gets "n/a".
 *  - Read the BU column as broken when no App Pack matched. "—" there is the
 *    correct state for an unconfigured user, not a loading gap.
 */
import React from 'react';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';

const STATUS_COLOR = { pass: 'success', fail: 'error', warn: 'warning', info: 'info' };

const VERDICT_LABEL = {
  pass: 'pass',
  fail: 'fail',
  warn: 'warn',
  info: 'info',
  not_evaluated: 'not evaluated',
};

const PLATFORM_LABEL = { ios: 'iOS', android: 'Android', fireos: 'Fire OS', vega: 'Vega' };

function platformLabel(p) {
  const key = String(p || '').toLowerCase();
  return PLATFORM_LABEL[key] || p;
}

function VerdictChip({ verdict, layer, applicable }) {
  if (!applicable) {
    return (
      <Tooltip title="This field only exists on another platform — this session's traffic was never expected to carry it. Not a failure, and not a pass.">
        <Chip size="small" variant="outlined" color="default" label="n/a" />
      </Tooltip>
    );
  }
  if (!verdict) {
    return <Typography variant="caption" sx={{ color: 'var(--muted)' }}>—</Typography>;
  }
  if (verdict === 'not_evaluated') {
    return (
      <Tooltip title="No eligible requests — this session never gave the rule anything to check. Not the same as a pass.">
        <Chip size="small" variant="outlined" color="default" label="not evaluated" />
      </Tooltip>
    );
  }
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
      <Chip size="small" color={STATUS_COLOR[verdict] || 'default'} label={VERDICT_LABEL[verdict] || verdict} />
      {layer && (
        <Typography variant="caption" sx={{ color: 'var(--muted)' }}>
          {layer === 'bu' ? 'BU' : 'spec'}
        </Typography>
      )}
    </Stack>
  );
}

/** One layer's cell (spec or BU) — identical between the two layers so the
 * table reads as one renderer, not two (spec §3.4). */
function LayerCell({ expected, eligible, failing, unconfiguredHint }) {
  if (eligible === null || eligible === undefined) {
    return (
      <Typography variant="caption" sx={{ color: 'var(--muted)' }}>
        {unconfiguredHint || '—'}
      </Typography>
    );
  }
  return (
    <Stack spacing={0.25}>
      {expected && (
        <Typography
          variant="body2"
          sx={{ fontSize: 12, fontFamily: 'ui-monospace, Menlo, monospace', wordBreak: 'break-word' }}
          title={expected}
        >
          {expected}
        </Typography>
      )}
      {eligible === 0 ? (
        <Typography variant="caption" sx={{ color: 'var(--muted)' }}>0 eligible requests</Typography>
      ) : (
        <Typography variant="caption" sx={{ color: failing > 0 ? 'error.main' : 'var(--muted)' }}>
          {failing > 0 ? `${failing} of ${eligible} failing` : `${eligible} eligible · 0 failing`}
        </Typography>
      )}
    </Stack>
  );
}

export default function ApsParamConformanceTable({ rows, sessionPlatform }) {
  const list = Array.isArray(rows) ? rows : [];
  const buConfigured = list.some((r) => r && r.buEligible !== null && r.buEligible !== undefined);

  if (!list.length) {
    return (
      <Typography variant="caption" sx={{ color: 'var(--muted)' }}>
        No APS display fields evaluated for this run yet.
      </Typography>
    );
  }

  return (
    <Stack spacing={1}>
      {!buConfigured && (
        <Typography variant="caption" sx={{ color: 'var(--muted)' }}>
          No App Pack matched this session's app bundle — showing spec-layer conformance only.
          Set up a BU pack under Config to check slot UUIDs against the ones this business unit
          registered with Amazon.
        </Typography>
      )}
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 520 }}>
        <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '18%' }}>Field</TableCell>
              <TableCell sx={{ width: '28%' }}>Spec (APS)</TableCell>
              <TableCell sx={{ width: '28%' }}>Business unit</TableCell>
              <TableCell sx={{ width: '11%' }}>Platform</TableCell>
              <TableCell sx={{ width: '15%' }} align="right">Verdict</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list.map((r) => (
              <TableRow key={r.key} hover sx={r.applicable ? undefined : { opacity: 0.6 }}>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <Tooltip title={r.meaning || 'No knowledge-pack entry for this field.'}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.label}</Typography>
                  </Tooltip>
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', color: 'var(--muted)', fontFamily: 'ui-monospace, Menlo, monospace' }}
                  >
                    {r.key}
                  </Typography>
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <LayerCell
                    expected={r.specExpected}
                    eligible={r.specEligible}
                    failing={r.specFailing}
                    unconfiguredHint="not evaluated by any spec check"
                  />
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <LayerCell
                    expected={r.buExpected}
                    eligible={r.buEligible}
                    failing={r.buFailing}
                    unconfiguredHint="—"
                  />
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  {r.platforms ? (
                    <Tooltip
                      title={r.applicable
                        ? `Sent only on ${r.platforms.map(platformLabel).join(', ')}.`
                        : `Sent only on ${r.platforms.map(platformLabel).join(', ')} — this session captured ${platformLabel(sessionPlatform) || 'another platform'}.`}
                    >
                      <Typography variant="caption" sx={{ color: r.applicable ? 'inherit' : 'var(--muted)' }}>
                        {r.platforms.map(platformLabel).join(', ')}
                      </Typography>
                    </Tooltip>
                  ) : (
                    <Typography variant="caption" sx={{ color: 'var(--muted)' }}>all</Typography>
                  )}
                </TableCell>
                <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                  <VerdictChip verdict={r.verdict} layer={r.verdictLayer} applicable={r.applicable} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}
